import CustomerDialog from '../components/CustomerDialog';
import CustomerFormImport from '../components/CustomerFormImport';
import CustomerWhatsApp from '../components/CustomerWhatsApp';
import NfseFiscalFields from '../components/NfseFiscalFields';
import { fetchCustomerCatalog } from '../utils/customerRegistryRequests';
import { useCustomerRegistryRefresh } from '../utils/customerRegistryEvents';
import { fiscalName } from '../utils/fiscalName';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaAddressBook, FaPlus, FaSyncAlt, FaSearch, FaSave, FaArrowRight, FaFileExcel, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaTimes, FaWhatsapp, FaFileInvoiceDollar, FaFileImport, FaDownload, FaUser, FaRegIdCard, FaRegStickyNote, FaUsers, FaUserCheck, FaUserSlash } from 'react-icons/fa';
import api from '../api';
import InfoTip from '../components/InfoTip';
import './Customers.css';
import { renderNfseCustomerDescription } from '../utils/nfseDescriptionPresets';

const digitsOnly = value => String(value || '').replace(/\D/g, '');
const maskDocument = value => {
  const digits = digitsOnly(value).slice(0, 14);
  return digits.length > 11
    ? digits.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
    : digits.replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
};
const maskCep = value => digitsOnly(value).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');
const maskPhone = value => {
  const digits = digitsOnly(value).slice(0, 13);
  if (digits.startsWith('55') && digits.length > 11) return '+55 ' + maskPhone(digits.slice(2));
  if (digits.length <= 2) return digits ? '(' + digits : '';
  const number = digits.slice(2);
  const prefix = '(' + digits.slice(0, 2) + ') ';
  return prefix + (number.length > 4 ? number.slice(0, number.length - 4) + '-' + number.slice(-4) : number);
};
const formatMoney = value => {
  const digits = digitsOnly(value).slice(0, 15);
  return digits ? (Number(digits) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
};
const asMoney = value => {
  if (value == null || value === '') return '';
  if (/^\d+(?:\.\d{1,2})?$/.test(String(value))) {
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(value);
};
const savedContacts = (customer, type) => {
  const list = customer[type];
  if (Array.isArray(list) && list.length) return list.map(item => typeof item === 'string'
    ? { value: item, label: '', isDefault: false } : { value: item.value || '', label: item.label || '', isDefault: Boolean(item.isDefault) });
  const single = customer[type === 'phones' ? 'phone' : 'email'];
  return single ? [{ value: single, label: '', isDefault: true }] : [];
};
const emptyCustomer = () => ({
  name: '', tradeName: '', number: '', complement: '', neighborhood: '', taxId: '', email: '', phone: '', phones: [], emails: [], zipCode: '',
  address: '', city: '', state: '', notes: '', status: 'ACTIVE',
  defaultServiceCode: '', defaultServiceDescription: '', defaultAmount: '', defaultTemplateId: '',
  noteDescriptionPresets: [], nfseDefaults: {},
});
const inputStyle = 'customer-input mt-1';
const mainButton = 'customer-button-primary';
const neutralButton = 'customer-button-secondary';
const errorMessage = (err) => err?.response?.data?.error || err?.message || 'A operação não pôde ser concluída.';

export default function Customers({ openTab }) {
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const requestSequence = useRef(0);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const formBaseline = useRef(null);
  const saveLock = useRef(false);
  const [form, setForm] = useState(emptyCustomer);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [cepStatus, setCepStatus] = useState({ type: 'idle', message: '', address: null });

  const [spreadsheetOpen, setSpreadsheetOpen] = useState(false);
  const [importMode, setImportMode] = useState('spreadsheet');
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formImportBusy, setFormImportBusy] = useState(false);
  const [whatsappCustomer, setWhatsappCustomer] = useState(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [openingNoteId, setOpeningNoteId] = useState(null);
  const noteRequest = useRef(0);
  const editingCustomer = customers.find(customer => customer.id === editingId) || selectedSnapshot;

  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [spreadsheetFile, setSpreadsheetFile] = useState(null);
  const [spreadsheetPreview, setSpreadsheetPreview] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 320);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    api.get('/nfse/templates').then(response => setTemplates(Array.isArray(response.data) ? response.data : []))
      .catch(() => setTemplates([])); // Usuários de Financeiro/CRM podem não ter acesso fiscal.
  }, []);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setBusy(true);
    try {
      const records = await fetchCustomerCatalog(api, {q:debouncedQuery || undefined}, () => sequence === requestSequence.current);
      if (!records) return;
      setCustomers(records);
      setError('');
    } catch (err) {
      if (sequence === requestSequence.current) setError(errorMessage(err));
    } finally {
      if (sequence === requestSequence.current) setBusy(false);
    }
  }, [debouncedQuery]);

  useEffect(() => { load(); }, [load]);
  useCustomerRegistryRefresh(kind => {
    if (kind !== 'templates') load();
    if (kind !== 'registry') api.get('/nfse/templates').then(response => setTemplates(Array.isArray(response.data) ? response.data : [])).catch(() => {});
  });

  // Consulta de CEP debounced e cancelável; campos já preenchidos pelo usuário
  // não são substituídos automaticamente (existe botão para aplicar novamente).
  useEffect(() => {
    const cep = digitsOnly(form.zipCode);
    if (!editorOpen || cep.length !== 8) {
      setCepStatus({ type: 'idle', message: '', address: null });
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setCepStatus({ type: 'loading', message: 'Consultando CEP...', address: null });
      try {
        const response = await fetch('https://viacep.com.br/ws/' + cep + '/json/', { signal: controller.signal });
        if (!response.ok) throw new Error('Não foi possível consultar o CEP.');
        const data = await response.json();
        if (data.erro) throw new Error('CEP não encontrado. Confira os números.');
        const address = { address: data.logradouro || '', neighborhood: data.bairro || '', city: data.localidade || '', state: data.uf || '' };
        setCepStatus({ type: 'success', message: 'Endereço localizado.', address });
        setForm(previous => digitsOnly(previous.zipCode) !== cep ? previous : ({
          ...previous,
          address: previous.address || address.address,
          neighborhood: previous.neighborhood || address.neighborhood,
          city: previous.city || address.city,
          state: previous.state || address.state,
        }));
      } catch (error) {
        if (!controller.signal.aborted) setCepStatus({ type: 'error', message: error.message || 'Consulta indisponível. Preencha manualmente.', address: null });
      }
    }, 400);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [editorOpen, form.zipCode]);
  const applyCepAddress = () => {
    if (!cepStatus.address) return;
    setForm(previous => ({ ...previous, ...cepStatus.address }));
  };

  const activeCount = useMemo(() => customers.filter(c => c.status === 'ACTIVE').length, [customers]);
  const inactiveCount = customers.length - activeCount;
  const states = useMemo(() => [...new Set(customers.map(c => String(c.state || '').toUpperCase()).filter(Boolean))].sort(), [customers]);
  const visibleCustomers = useMemo(() => customers.filter(customer => {
    if (statusFilter !== 'all' && customer.status !== statusFilter) return false;
    const docLength = digitsOnly(customer.taxId).length;
    if (typeFilter === 'person' && docLength !== 11) return false;
    if (typeFilter === 'company' && docLength !== 14) return false;
    if (stateFilter !== 'all' && String(customer.state || '').toUpperCase() !== stateFilter) return false;
    if (cityFilter && !String(customer.city || '').toLocaleLowerCase('pt-BR').includes(cityFilter.trim().toLocaleLowerCase('pt-BR'))) return false;
    return true;
  }), [customers, statusFilter, typeFilter, stateFilter, cityFilter]);

  const hasFormChanges = () => editorOpen && formBaseline.current !== null
    && JSON.stringify(form) !== formBaseline.current;
  const closeEditor = () => {
    if (saving) return false;
    if (hasFormChanges() && !window.confirm('Há alterações não salvas neste cliente. Deseja descartá-las?')) return false;
    formBaseline.current = null;
    setEditorOpen(false);
    setEditingId(null);
    return true;
  };
  useEffect(() => {
    if (!editorOpen) return undefined;
    const warn = event => {
      if (formBaseline.current === null || JSON.stringify(form) === formBaseline.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editorOpen, form]);
  const toggleSpreadsheet = () => {
    if (editorOpen && !closeEditor()) return;
    setImportMode('spreadsheet');
    setSpreadsheetOpen(true);
    setError('');
    setBulkResults(null);
  };

  useEffect(() => {
    if (!spreadsheetOpen || importMode !== 'form') return undefined;
    let active = true; setFormsLoading(true); setError('');
    api.get('/forms').then(({ data }) => { if (active) setForms(Array.isArray(data) ? data : []); })
      .catch(failure => { if (active) setError(errorMessage(failure)); })
      .finally(() => { if (active) setFormsLoading(false); });
    return () => { active = false; };
  }, [spreadsheetOpen, importMode]);
  const closeImport = () => { if (!bulkBusy && !formImportBusy) { setSpreadsheetOpen(false); setError(''); } };
  const launchNote = async customer => {
    if (!openTab) { setError('Não foi possível abrir o Gerenciador NFS nesta tela.'); return; }
    const request = ++noteRequest.current; setOpeningNoteId(customer.id); setError('');
    try {
      const { default: NfseManager } = await import('./NfseManager');
      if (request !== noteRequest.current) return;
      openTab('nfse-manager', 'Gerenciador NFS', NfseManager, { openTab, initialCustomerId: customer.id, customerRequestKey: Date.now() + ':' + request });
    } catch { setError('Não foi possível abrir a criação da nota. Tente novamente.'); }
    finally { if (request === noteRequest.current) setOpeningNoteId(null); }
  };
  const openCrmChannels = async () => {
    if (!openTab) return;
    try {
      const { default: CRM } = await import('./CRM');
      if (editorOpen && !closeEditor()) return;
      setWhatsappCustomer(null);
      openTab('crm', 'CRM', CRM, { openTab, initialTab: 'integrations', initialTabRequest: Date.now() });
    } catch {
      setWhatsappCustomer(null);
      setError('Não foi possível abrir os canais do CRM. Tente novamente.');
    }
  };
  const jumpTo = section => document.getElementById('customer-' + section)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });

  const openCreate = () => {
    if (editorOpen && !closeEditor()) return;
    const nextForm = emptyCustomer();
    formBaseline.current = JSON.stringify(nextForm);
    setEditingId(null);
    setSelectedSnapshot(null);
    setForm(nextForm);
    setNotice('');
    setError('');
    setEditorOpen(true);
    setShowAdvanced(false);
    setCepStatus({ type: 'idle', message: '', address: null });
  };
  const edit = (customer) => {
    if (editorOpen && !closeEditor()) return;
    const nextForm = { ...emptyCustomer(), ...customer, defaultAmount: asMoney(customer.defaultAmount),
      defaultServiceDescription: (customer.noteDescriptionPresets || []).find(preset => preset.isDefault)?.text || customer.defaultServiceDescription || '',
      phones: savedContacts(customer, 'phones'), emails: savedContacts(customer, 'emails'),
      noteDescriptionPresets: Array.isArray(customer.noteDescriptionPresets) ? customer.noteDescriptionPresets : [] };
    formBaseline.current = JSON.stringify(nextForm);
    setEditingId(customer.id);
    setSelectedSnapshot(customer);
    setForm(nextForm);
    setEditorOpen(true);
    setNotice('');
    setError('');
    setShowAdvanced(Boolean(customer.defaultServiceCode || customer.defaultAmount || customer.defaultTemplateId || customer.noteDescriptionPresets?.length || Object.values(customer.nfseDefaults || {}).some(Boolean)));
  };
  const save = async event => {
    event.preventDefault();
    if (saving || saveLock.current) return;
    const issue = event.nativeEvent?.submitter?.value === 'issue';
    if (issue && editingId && !hasFormChanges()) { if (closeEditor()) await launchNote(editingCustomer); return; }
    saveLock.current = true; setSaving(true);
    setError('');
    try {
      // Evita envios acidentais duplicados antes da validação de contatos no servidor.
      for (const kind of ['phones', 'emails']) {
        const values = (form[kind] || []).map(item => kind === 'phones' ? digitsOnly(item.value) : (item.value || '').trim().toLowerCase()).filter(Boolean);
        if (new Set(values).size !== values.length) throw new Error(kind === 'phones' ? 'Remova os telefones duplicados.' : 'Remova os e-mails duplicados.');
      }
      let response;
      if (editingId) {
        const baseline = formBaseline.current ? JSON.parse(formBaseline.current) : {};
        const changes = Object.fromEntries(Object.entries(form).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(baseline[key])));
        response = await api.put('/customers/' + editingId, changes);
      }
      else response = await api.post('/customers', form);
      setNotice(editingId ? 'Cadastro atualizado nos módulos integrados.' : 'Cliente cadastrado e integrado.');
      formBaseline.current = null;
      setEditorOpen(false);
      await load();
      if (issue) {
        const saved = response?.data || { ...form, id: editingId };
        if (saved.id) await launchNote(saved);
        else setError('Cadastro salvo. Abra o cliente na lista para emitir a nota.');
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      saveLock.current = false; setSaving(false);
    }
  };
  const field = name => event => {
    const value = event.target.value;
    setForm(prev => ({ ...prev, [name]: name === 'taxId' ? digitsOnly(value).slice(0, 14)
      : name === 'zipCode' ? digitsOnly(value).slice(0, 8)
      : name === 'state' ? value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2)
      : name === 'defaultAmount' ? formatMoney(value) : name === 'name' ? fiscalName(value) : value }));
  };
  const updateContact = (kind, index, changes) => setForm(prev => ({
    ...prev, [kind]: (prev[kind] || []).map((item, i) => i === index ? { ...item, ...changes } : item),
  }));
  const setPrimaryContact = (kind, index) => setForm(prev => ({
    ...prev, [kind]: (prev[kind] || []).map((item, i) => ({ ...item, isDefault: i === index })),
  }));
  const addContact = kind => setForm(prev => ({
    ...prev, [kind]: [...(prev[kind] || []), { value: '', label: '', isDefault: !(prev[kind] || []).length }],
  }));
  const removeContact = (kind, index) => setForm(prev => {
    const contacts = (prev[kind] || []).filter((_, i) => i !== index);
    if (contacts.length && !contacts.some(item => item.isDefault)) contacts[0] = { ...contacts[0], isDefault: true };
    return { ...prev, [kind]: contacts };
  });
  const renderContacts = (kind, title, hint) => {
    const contacts = form[kind] || [];
    const phone = kind === 'phones';
    return <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">{phone ? <FaWhatsapp/> : <FaEnvelope/>}{title}<InfoTip text={hint}/></h3>
        <button type="button" disabled={contacts.length >= 50} onClick={() => addContact(kind)} className={neutralButton}>
          <FaPlus size={11}/> Adicionar {phone ? 'número' : 'e-mail'}
        </button>
      </div>
      {!contacts.length && <p className="mt-3 text-xs text-slate-500">Nenhum contato cadastrado.</p>}
      <div className="mt-3 space-y-2">
        {contacts.map((contact, index) => <div key={index} className="customer-contact-row">
          <label className="min-w-0 text-xs font-medium text-slate-700">
            {phone ? 'Telefone / WhatsApp' : 'E-mail'} {index + 1}
            <input className={inputStyle} type={phone ? 'tel' : 'email'} inputMode={phone ? 'tel' : 'email'}
              autoComplete="off" maxLength={phone ? 23 : 200} placeholder={phone ? '(14) 99999-9999' : 'contato@empresa.com.br'}
              value={phone ? maskPhone(contact.value) : contact.value || ''}
              onChange={event => updateContact(kind, index, { value: phone ? digitsOnly(event.target.value).slice(0, 13) : event.target.value })}/>
          </label>
          <label className="min-w-0 text-xs font-medium text-slate-700">Identificação (opcional)
            <input className={inputStyle} maxLength={40} placeholder={phone ? 'Comercial, celular...' : 'Financeiro, pessoal...'}
              value={contact.label || ''} onChange={event => updateContact(kind, index, { label: event.target.value })}/>
          </label>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <button type="button" disabled={!contact.value} aria-pressed={Boolean(contact.isDefault)}
              onClick={() => setPrimaryContact(kind, index)} className={'min-h-[44px] rounded-xl border px-3 text-xs font-semibold ' +
              (contact.isDefault ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-blue-50')}>
              {contact.isDefault ? '✓ Padrão' : 'Definir padrão'}
            </button>
            <button type="button" aria-label={'Remover ' + title.toLowerCase() + ' ' + (index + 1)}
              onClick={() => removeContact(kind, index)} className="min-h-[44px] rounded-xl border border-red-100 px-3 text-xs font-medium text-red-700">Remover</button>
          </div>
        </div>)}
      </div>
    </section>;
  };
  const presets = Array.isArray(form.noteDescriptionPresets) ? form.noteDescriptionPresets : [];
  const updatePreset = (index, updates) => setForm(prev => ({
    ...prev, noteDescriptionPresets: (prev.noteDescriptionPresets || []).map((item, position) =>
      position === index ? { ...item, ...updates } : item),
  }));
  const setMainPreset = (index) => setForm(prev => ({ ...prev,
    noteDescriptionPresets: (prev.noteDescriptionPresets || []).map((item, position) =>
      ({ ...item, isDefault: position === index })),
  }));
  const removePreset = (index) => setForm(prev => ({ ...prev,
    noteDescriptionPresets: (prev.noteDescriptionPresets || []).filter((_, position) => position !== index),
  }));
  const addPreset = () => setForm(prev => ({ ...prev,
    noteDescriptionPresets: [...(prev.noteDescriptionPresets || []), { name: '', text: '', isDefault: !(prev.noteDescriptionPresets || []).length }],
  }));

  const downloadTemplate = async () => {
    setBulkBusy(true); setError('');
    try {
      const response = await api.get('/customers/import/spreadsheet/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = 'modelo-importacao-clientes.xlsx';
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBulkBusy(false); }
  };

  const sendSpreadsheet = async (action) => {
    if (!spreadsheetFile || (action === 'commit' && !spreadsheetPreview)) return;
    if (action === 'commit' && !window.confirm('Confirmar importação de até ' + spreadsheetPreview.ready + ' novos clientes? A planilha será validada novamente e nenhum cadastro existente será sobrescrito.')) return;
    setBulkBusy(true); setError('');
    try {
      const data = new FormData();
      data.append('file', spreadsheetFile);
      const response = await api.post('/customers/import/spreadsheet/' + action, data);
      if (action === 'preview') {
        setSpreadsheetPreview(response.data);
      } else {
        setBulkResults(response.data);
        setNotice((response.data?.created || 0) + ' clientes criados por planilha. ' + (response.data?.skipped || 0) + ' já existentes.');
        setSpreadsheetPreview(null);
        await load();
      }
    } catch (err) { setError(errorMessage(err)); }
    finally { setBulkBusy(false); }
  };

  const editableField = (label, name, options = {}) => <label key={name} className="block min-w-0 text-xs font-medium text-slate-700">
    {label}
    <input value={name === 'taxId' ? maskDocument(form[name]) : name === 'zipCode' ? maskCep(form[name]) : name === 'name' ? fiscalName(form[name]) : form[name] || ''}
      onChange={field(name)} required={options.required} inputMode={options.inputMode}
      autoComplete={options.autoComplete} placeholder={options.placeholder} type={options.type || 'text'} maxLength={options.maxLength}
      className={inputStyle} />
  </label>;

  return <div className="customers-ui mx-auto w-full max-w-7xl min-w-0 space-y-5 pb-10">
    <header className="customers-hero flex flex-col gap-5 rounded-3xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between sm:p-7">
      <div className="flex min-w-0 items-center gap-4">
        <span className="customers-hero-icon flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-blue-700"><FaAddressBook size={27}/></span>
        <div className="min-w-0">
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Clientes</h1>
        </div>
      </div>
      <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
        <button type="button" onClick={load} disabled={busy} className={neutralButton} aria-label="Atualizar lista" title="Atualizar lista"><FaSyncAlt size={17} className={busy ? 'animate-spin' : ''}/></button>
        <button type="button" onClick={toggleSpreadsheet} aria-label="Importar clientes" aria-expanded={spreadsheetOpen} className={neutralButton}><FaFileImport/> Importar</button>
        <button type="button" onClick={openCreate} className={mainButton}><FaPlus size={16}/> Novo cliente</button>
      </div>
    </header>
    <div className="grid grid-cols-3 gap-2 sm:gap-4" aria-label="Resumo dos clientes">
      {[
        { label: 'Cadastros', value: customers.length, filter: 'all', Icon: FaUsers },
        { label: 'Ativos', value: activeCount, filter: 'ACTIVE', Icon: FaUserCheck },
        { label: 'Inativos', value: inactiveCount, filter: 'INACTIVE', Icon: FaUserSlash },
      ].map(item => <button key={item.filter} type="button" aria-pressed={statusFilter === item.filter} onClick={() => setStatusFilter(item.filter)}
        className={'customers-stat rounded-2xl border p-3 text-left transition sm:p-5 ' + (statusFilter === item.filter ? 'border-blue-200 bg-blue-50/60' : 'border-slate-200 bg-white hover:border-blue-200')}>
<span className="customer-stat-icon"><item.Icon/></span><span className="customer-stat-label">{item.label}</span><strong className="customer-stat-value">{item.value}</strong>
      </button>)}
    </div>
    {error && !editorOpen && !spreadsheetOpen && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {notice && <p role="status" className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">{notice}</p>}

    {editorOpen && <CustomerDialog title={editingId ? 'Ficha do cliente' : 'Novo cliente'} subtitle={editingId ? fiscalName(form.name) : undefined} icon={FaAddressBook} onClose={closeEditor} busy={saving}
      actions={editingId && <button type="button" className={neutralButton} onClick={() => setWhatsappCustomer(editingCustomer)} disabled={saving}><FaWhatsapp/> WhatsApp</button>}
      footer={<><button type="button" className={neutralButton} onClick={closeEditor} disabled={saving}>Cancelar</button><span className="customer-footer-spacer"/>{openTab && <button type="submit" form="customer-editor-form" value="issue" disabled={saving || openingNoteId !== null} className={neutralButton}><FaFileInvoiceDollar/>{hasFormChanges() || !editingId ? 'Salvar e criar nota' : 'Emitir nota'}</button>}<button type="submit" form="customer-editor-form" disabled={saving} className={mainButton}><FaSave/>{saving ? 'Salvando…' : 'Salvar cliente'}</button></>}>
      <nav className="customer-editor-nav" aria-label="Seções da ficha">
        {[[FaUser,'Dados','details'],[FaMapMarkerAlt,'Endereço','address'],[FaFileInvoiceDollar,'Nota fiscal','fiscal'],[FaRegStickyNote,'Observações','notes']].map(([Icon,label,id]) => <button key={id} type="button" onClick={() => jumpTo(id)}><Icon/>{label}</button>)}
      </nav>
      <form id="customer-editor-form" onSubmit={save} className="customer-editor-form space-y-5">
        {error && <p role="alert" className="customer-inline-error">{error}</p>}
        <section id="customer-details" className="customer-section"><h3 className="customer-section-title"><FaRegIdCard/> Identificação</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {editableField('Nome / razão social *', 'name', { required: true, maxLength: 200 })}
          {editableField('Nome fantasia', 'tradeName', { maxLength: 200 })}
          {editableField('CPF / CNPJ', 'taxId', { maxLength: 18, inputMode: 'numeric', placeholder: '000.000.000-00 ou 00.000.000/0000-00' })}
          <label className="min-w-0 text-xs font-medium text-slate-700">Status
            <select className={inputStyle} value={form.status} onChange={field('status')}>
              <option value="ACTIVE">Ativo</option><option value="INACTIVE">Inativo</option>
            </select>
          </label>
        </div>
        </section>
        <div className="grid gap-4 lg:grid-cols-2">
          {renderContacts('phones', 'Telefones e WhatsApp', 'Cadastre vários números, identifique cada um e marque o contato principal. O principal será usado nas integrações que aceitam um único número.')}
          {renderContacts('emails', 'Endereços de e-mail', 'Cadastre vários e-mails e marque o principal para a NFS-e e outras integrações. Os demais permanecem salvos na ficha.')}
        </div>
        <section id="customer-address" className="customer-section space-y-3">
          <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900"><FaMapMarkerAlt/> Endereço <InfoTip text="Digite o CEP para buscar rua, bairro, cidade e UF. Endereços digitados manualmente não serão sobrescritos sem sua ação."/></h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {editableField('CEP', 'zipCode', { maxLength: 9, inputMode: 'numeric', autoComplete: 'postal-code', placeholder: '00000-000' })}
            {editableField('Cidade', 'city', { maxLength: 120, autoComplete: 'address-level2' })}
            {editableField('UF', 'state', { maxLength: 2, autoComplete: 'address-level1', placeholder: 'SP' })}
            <div className="sm:col-span-3">{editableField('Rua / avenida', 'address', { maxLength: 255, autoComplete: 'street-address', placeholder: 'Rua ou avenida' })}</div>
            {editableField('Número', 'number', { maxLength: 30 })}
            {editableField('Complemento', 'complement', { maxLength: 120 })}
            {editableField('Bairro', 'neighborhood', { maxLength: 120 })}
          </div>
          {cepStatus.type !== 'idle' && <div role="status" aria-live="polite" className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className={cepStatus.type === 'error' ? 'text-amber-700' : cepStatus.type === 'success' ? 'text-emerald-700' : ''}>{cepStatus.message}</span>
            {cepStatus.address && <button type="button" className="rounded-lg border border-blue-200 bg-white px-3 py-2 font-semibold text-blue-700" onClick={applyCepAddress}>Aplicar endereço encontrado</button>}
          </div>}
        </section>
        <section id="customer-fiscal" className="customer-section space-y-4"><h3 className="customer-section-title"><FaFileInvoiceDollar/> Nota fiscal</h3>
        <label className="block text-sm font-semibold text-slate-800">Descrição padrão das notas deste cliente
          <textarea rows={3} maxLength={4000} value={form.defaultServiceDescription || ''}
            onChange={event => { const text = event.target.value; setForm(previous => ({ ...previous, defaultServiceDescription: text, noteDescriptionPresets: (previous.noteDescriptionPresets || []).map(preset => preset.isDefault ? { ...preset, text } : preset) })); }}
            placeholder="Descreva o serviço normalmente prestado a este cliente" className={inputStyle + ' resize-y'}/>
          <span className="mt-1 block text-xs font-normal text-slate-500">Texto do cliente seguido do geral, editável na nota. Variáveis: {'{{cliente}}, {{mes}}, {{ano}} e {{competencia}}.'}</span>
        </label>
        <button type="button" aria-expanded={showAdvanced} className="flex w-full items-center justify-between rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-left text-sm font-semibold text-blue-900" onClick={() => setShowAdvanced(v => !v)}>
          Padrões de serviços deste cliente <span>{showAdvanced ? '−' : '+'}</span>
        </button>
        {showAdvanced && <div className="grid gap-3 rounded-xl bg-slate-50 p-3 sm:grid-cols-2">
          {templates.length > 0 && <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
            Modelo geral de serviço
            <select className={inputStyle} value={form.defaultTemplateId || ''} onChange={event => setForm(prev => ({ ...prev, defaultTemplateId: event.target.value ? Number(event.target.value) : null }))}>
              <option value="">Sem modelo padrão</option>
              {templates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
            </select>
          </label>}
          {editableField('Código padrão do serviço', 'defaultServiceCode', { maxLength: 20 })}
          {editableField('Valor padrão (R$)', 'defaultAmount', { placeholder: 'Ex.: 250,00' })}
          <div className="sm:col-span-2"><NfseFiscalFields values={form.nfseDefaults || {}} inherit onChange={(key, value) => setForm(previous => ({ ...previous, nfseDefaults: { ...previous.nfseDefaults, [key]: value } }))}/></div>
          <div className="min-w-0 space-y-3 sm:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-1 text-sm font-semibold text-slate-900">Descrições de notas
                <InfoTip text="Salve até 10 descrições por cliente. Marque uma como principal. Variáveis aceitas: {{cliente}}, {{mes}}, {{ano}}, {{competencia}}. O texto permanece editável em cada rascunho." />
              </h3>
              <div className="flex flex-wrap gap-2">
                {form.defaultServiceDescription && !presets.some(preset => preset.text === form.defaultServiceDescription) &&
                  <button type="button" disabled={presets.length >= 10} onClick={() => setForm(prev => ({
                    ...prev, noteDescriptionPresets: [...(prev.noteDescriptionPresets || []), {
                      name: 'Descrição ' + ((prev.noteDescriptionPresets || []).length + 1),
                      text: prev.defaultServiceDescription,
                      isDefault: !(prev.noteDescriptionPresets || []).length,
                    }],
                  }))} className={neutralButton}>Adicionar padrão à lista</button>}
                <button type="button" onClick={addPreset} disabled={presets.length >= 10} className={neutralButton}>
                  <FaPlus size={12}/> Nova descrição
                </button>
              </div>
            </div>
            {presets.length === 0 && <p className="text-xs text-slate-500">Salve a descrição usada com frequência para reutilizá-la nas próximas notas.</p>}
            {presets.map((preset, index) => <div key={index} className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <label className="min-w-[160px] flex-1 text-xs font-semibold text-slate-700">
                  Nome da descrição
                  <input value={preset.name || ''} onChange={event => updatePreset(index, { name: event.target.value })}
                    maxLength={80} placeholder="Ex.: Mensalidade de suporte" className={inputStyle}/>
                </label>
                <button type="button" aria-pressed={Boolean(preset.isDefault)} onClick={() => setMainPreset(index)}
                  className={'min-h-[44px] rounded-xl border px-3 text-xs font-semibold ' +
                    (preset.isDefault ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600')}>
                  {preset.isDefault ? 'Principal' : 'Tornar principal'}
                </button>
                <button type="button" onClick={() => removePreset(index)}
                  aria-label={'Excluir descrição ' + (preset.name || index + 1)}
                  className="min-h-[44px] rounded-xl border border-red-100 px-3 text-xs font-semibold text-red-700">Excluir</button>
              </div>
              <label className="block text-xs font-semibold text-slate-700">
                Texto para a NFS-e
                <textarea rows={4} maxLength={4000} value={preset.text || ''}
                  placeholder="Prestação de serviços de suporte técnico referente a {{competencia}}."
                  onChange={event => updatePreset(index, { text: event.target.value })}
                  className={inputStyle + ' resize-y leading-relaxed'}/>
                <span className="mt-1 block text-right text-[11px] font-normal text-slate-500">{(preset.text || '').length}/4000</span>
                {preset.text && <span className="mt-2 block rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs font-normal leading-relaxed text-slate-600">
                  <strong className="block text-slate-700">Prévia:</strong>
                  {renderNfseCustomerDescription(preset.text, { customerName: form.name, competenceDate: new Date().toISOString().slice(0, 10) })}
                </span>}
              </label>
            </div>)}
          </div>
          <InfoTip text="O código e valor próprios do cliente têm prioridade sobre o modelo geral. Se houver descrição principal, ela será sugerida ao selecionar o cliente no Gerenciador NFS." />
        </div>}
        </section>
        <label id="customer-notes" className="customer-section block text-xs font-semibold text-slate-700"><span className="customer-section-title"><FaRegStickyNote/> Observações</span><textarea className={inputStyle} rows={2} maxLength={2000} value={form.notes || ''} onChange={field('notes')} /></label>
      </form>
    </CustomerDialog>}

    <section className="customers-list rounded-3xl border border-slate-200 bg-white p-4 sm:p-6">
      <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div><h2 className="text-lg font-bold text-slate-900">Cadastros <span className="text-sm font-medium text-slate-400">({visibleCustomers.length})</span></h2>
</div>

      </div>
      <div className="customers-filters mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(170px,2fr)_minmax(110px,1fr)_minmax(130px,1fr)_minmax(90px,.65fr)_minmax(120px,1fr)_auto] lg:items-center">
        <div className="relative min-w-0"><FaSearch aria-hidden="true" className="absolute left-3 top-[18px] text-slate-400"/>
          <input aria-label="Buscar clientes" className={inputStyle + ' customer-search-input'} placeholder="Buscar cliente, CPF/CNPJ ou contato" value={query} onChange={e => setQuery(e.target.value)} /></div>
        <select aria-label="Filtrar clientes pelo status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={inputStyle + ' !mt-0'}>
          <option value="all">Todos os status</option><option value="ACTIVE">Ativos</option><option value="INACTIVE">Inativos</option>
        </select>
        <select aria-label="Filtrar clientes pelo tipo" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={inputStyle + ' !mt-0'}>
          <option value="all">Todos os tipos</option><option value="person">Pessoa física</option><option value="company">Pessoa jurídica</option>
        </select>
        <select aria-label="Filtrar clientes por UF" value={stateFilter} onChange={e => setStateFilter(e.target.value)} className={inputStyle + ' !mt-0'}>
          <option value="all">Todas UFs</option>{states.map(state => <option key={state} value={state}>{state}</option>)}
        </select>
        <input aria-label="Filtrar clientes por cidade" className={inputStyle + ' !mt-0'} placeholder="Cidade" value={cityFilter} onChange={e => setCityFilter(e.target.value)} />
        <button type="button" className="inline-flex min-h-[44px] items-center justify-center gap-1 rounded-xl px-3 text-sm font-semibold text-slate-500 hover:bg-slate-100"
          onClick={() => { setStatusFilter('all'); setTypeFilter('all'); setStateFilter('all'); setCityFilter(''); setQuery(''); }}><FaTimes/> Limpar</button>
      </div>

      {busy ? <p className="py-8 text-center text-sm text-slate-500">Carregando…</p> : visibleCustomers.length === 0
        ? <div className="py-10 text-center"><FaAddressBook className="mx-auto text-3xl text-slate-300"/><p className="mt-2 text-sm font-semibold text-slate-700">Nenhum cliente encontrado</p></div>
        : <div className="customers-rows" role="table" aria-label="Clientes cadastrados">
          {visibleCustomers.map(customer => {
            const name = fiscalName(customer.name);
            const initials = name.trim().split(/\s+/).slice(0, 2).map(item => item[0] || '').join('').toUpperCase() || '?';
            return <article key={customer.id} role="row" tabIndex={0} aria-label={'Cadastro de ' + name} title="Duplo clique para abrir a ficha" className="customers-row"
              onDoubleClick={event => { if (!event.target.closest('button,a,input,select,textarea')) edit(customer); }}
              onKeyDown={event => { if (event.target === event.currentTarget && ['Enter',' '].includes(event.key)) { event.preventDefault(); edit(customer); } }}>
              <div role="cell" className="flex min-w-0 items-center gap-3"><span aria-hidden="true" className="customers-avatar flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-bold">{initials}</span>
                <div className="min-w-0"><h3 className="break-words text-sm font-bold text-slate-900">{name}</h3><p className="text-xs text-slate-500">{customer.taxId ? maskDocument(customer.taxId) : 'Sem CPF/CNPJ'}</p></div></div>
              <div role="cell" className="min-w-0 text-sm text-slate-600">{customer.email && <p className="flex min-w-0 items-center gap-2"><FaEnvelope className="shrink-0 text-slate-400"/><span className="break-all">{customer.email}</span></p>}</div>
              <div role="cell" className="min-w-0 text-sm text-slate-600">{customer.phone && <p className="flex items-center gap-2"><FaPhoneAlt className="shrink-0 text-slate-400"/>{maskPhone(customer.phone)}</p>}</div>
              <div role="cell" className="min-w-0 text-xs text-slate-600">
                {(customer.city || customer.state) && <p className="flex items-center gap-1"><FaMapMarkerAlt className="shrink-0 text-slate-400"/>{[customer.city, customer.state].filter(Boolean).join(' / ')}</p>}
                <span className={customer.status === 'ACTIVE' ? 'font-semibold text-emerald-700' : 'font-semibold text-slate-500'}>{customer.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}</span>
              </div>
              <div role="cell" className="customer-row-actions">
                <button type="button" onClick={() => setWhatsappCustomer(customer)} aria-label={'WhatsApp de ' + name} title="WhatsApp da empresa" className="customer-icon-button"><FaWhatsapp/></button>
                <button type="button" onClick={() => edit(customer)} aria-label={'Abrir ficha de ' + name} className={neutralButton}>Abrir <FaArrowRight size={12}/></button>
                {openTab && <button type="button" disabled={openingNoteId !== null} onClick={() => launchNote(customer)} aria-label={'Emitir nota para ' + name} className={mainButton}><FaFileInvoiceDollar/> Emitir nota</button>}
              </div>
            </article>;
          })}
        </div>}
    </section>
    {spreadsheetOpen && <CustomerDialog title="Importar clientes" icon={FaFileImport} onClose={closeImport} busy={bulkBusy || formImportBusy}>
      <div className="customer-import-tabs" aria-label="Tipo de importação">{[['spreadsheet','Planilha',FaFileExcel],['form','Formulário',FaFileImport]].map(([key,label,Icon]) => <button type="button" key={key} aria-pressed={importMode === key} disabled={bulkBusy || formImportBusy} onClick={() => { setImportMode(key); setError(''); }}><Icon/>{label}</button>)}</div>
      {error && <p role="alert" className="customer-inline-error">{error}</p>}
      {importMode === 'spreadsheet' && <div className="space-y-4">
        <button type="button" disabled={bulkBusy} onClick={downloadTemplate} className={neutralButton}><FaDownload/> Gerar modelo XLSX</button>
        <label className="customer-dropzone">
          <FaFileExcel/><strong>{spreadsheetFile?.name || 'Selecionar planilha'}</strong><span>.xlsx · até 2 MB · 500 clientes</span>
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" aria-label="Selecionar planilha XLSX" className="sr-only" disabled={bulkBusy}
            onChange={event => { const file = event.target.files?.[0] || null; setSpreadsheetPreview(null); setBulkResults(null); if (file && file.size > 2 * 1024 * 1024) { setSpreadsheetFile(null); setError('A planilha deve ter até 2 MB.'); } else { setSpreadsheetFile(file); setError(''); } }}/>
        </label>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!spreadsheetFile || bulkBusy} onClick={() => sendSpreadsheet('preview')} className={neutralButton}>Conferir planilha</button>
          <button type="button" disabled={!spreadsheetPreview?.ready || bulkBusy} onClick={() => sendSpreadsheet('commit')} className={mainButton}>Confirmar importação</button>
        </div>
        {spreadsheetPreview && <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-3 text-sm">
          <strong>{spreadsheetPreview.total} linhas · {spreadsheetPreview.ready} prontas · {spreadsheetPreview.duplicates} existentes · {spreadsheetPreview.invalid} inválidas</strong>
          <div className="max-h-52 overflow-y-auto">{(spreadsheetPreview.rows || []).map(row => <p key={row.line} className="border-t border-slate-100 py-1 text-xs">#{row.line}: {fiscalName(row.name) || 'Sem nome'} — {row.error || (row.existing ? 'Existente' : 'Pronto')}</p>)}</div>
        </div>}
        {bulkResults && <div role="status" className="rounded-xl bg-white p-3 text-sm">Importação concluída: {bulkResults.created || 0} criados · {bulkResults.skipped || 0} existentes · {bulkResults.failed || 0} erros.</div>}
      </div>}
      {importMode === 'form' && (formsLoading ? <p role="status">Carregando formulários…</p> : <CustomerFormImport forms={forms} embedded onBusyChange={setFormImportBusy} onImported={load} onClose={closeImport}/>)}
    </CustomerDialog>}
    {whatsappCustomer && <CustomerDialog title="WhatsApp da empresa" subtitle={fiscalName(whatsappCustomer.name)} icon={FaWhatsapp} onClose={() => setWhatsappCustomer(null)}>
      <CustomerWhatsApp customer={whatsappCustomer} onConfigure={openTab ? openCrmChannels : undefined}/>
    </CustomerDialog>}
  </div>;
}
